import {
  InsertDriveFileOutlined as FileIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
} from '@mui/icons-material'
import { Box, CardMedia, Stack, Typography, IconButton } from '@mui/material'
import { FC, memo, useState } from 'react'

type FileListViewProps = {
  files: File[]
  setFiles: React.Dispatch<React.SetStateAction<File[]>>
  canRemoveFiles?: boolean
}

const FileListView: FC<FileListViewProps> = ({
  files,
  setFiles,
  canRemoveFiles = false,
}) => {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null)

  const handleDownload = (file: File) => {
    const url = URL.createObjectURL(file)
    const link = document.createElement('a')
    link.href = url
    link.download = file.name
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const handleDelete = (idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx))
  }

  return (
    <Stack
      direction={'row'}
      flexWrap="wrap"
      justifyContent={'center'}
      sx={{
        // Remove left margin for the first item of each row (assuming 4 items per row)
        '& > *:nth-of-type(4n+1)': {
          ml: 0,
        },
        // Add top margin for items not in the first row (assuming 4 items per row)
        '& > *:nth-of-type(n+5)': {
          mt: '10px',
        },
      }}
    >
      {files.map((file, idx) => (
        <Stack
          key={`${file.name}-${idx}`}
          sx={{
            position: 'relative',
            width: '100px',
            height: '100px',
            cursor: 'pointer',
          }}
          onMouseEnter={() => setHoveredIdx(idx)}
          onMouseLeave={() => setHoveredIdx(null)}
        >
          {file.type.startsWith('image/') ? (
            <CardMedia
              component={'img'}
              image={URL.createObjectURL(file)}
              alt={file.name}
              sx={{
                borderColor: 'divider',
                border: '1px solid',
                borderRadius: 1,
                overflow: 'hidden',
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.paper',
                border: '1px solid',
                borderRadius: 1,
              }}
            >
              <Stack justifyContent={'center'} alignItems={'center'}>
                <FileIcon />
                <Typography variant="caption" align="center">
                  {file.type.startsWith('application/pdf') ? 'PDF' : 'File'}
                </Typography>
              </Stack>
            </Box>
          )}
          {hoveredIdx === idx && canRemoveFiles && (
            <IconButton
              size="small"
              sx={{
                position: 'absolute',
                top: -7,
                right: 4,
                zIndex: 2,
                bgcolor: 'background.paper',
                '&:hover': { bgcolor: 'grey.200' },
              }}
              onClick={(e) => {
                e.stopPropagation()
                handleDelete(idx)
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
          {hoveredIdx === idx && (
            <IconButton
              size="small"
              sx={{
                position: 'absolute',
                bottom: 4,
                right: 4,
                bgcolor: 'background.paper',
                '&:hover': { bgcolor: 'grey.200' },
              }}
              onClick={(e) => {
                e.stopPropagation()
                handleDownload(file)
              }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      ))}
    </Stack>
  )
}

export default memo(FileListView)
